import { UUID } from '@/api/generatedApi';
import { useState } from 'react';

export type ContextMenuPosition = { left: number; top: number } | undefined;

const getClickedField = (event: React.MouseEvent<Element>): string | undefined => {
  const cellElement = (event.target as HTMLElement).closest('[data-field]') as HTMLElement | null;
  return cellElement?.dataset.field ?? undefined;
};

export const useContextMenu = <T>({
  canUseContextMenu,
  getRowById,
}: {
  canUseContextMenu: boolean;
  getRowById: (rowId: UUID) => T | undefined;
}) => {
  const [contextMenuPosition, setContextMenuPosition] = useState<ContextMenuPosition>();
  const [contextMenuClickedItem, setContextMenuClickedItem] = useState<T | undefined>();
  const [contextMenuClickedField, setContextMenuClickedField] = useState<string | undefined>();

  const handleCloseContextMenu = () => {
    setContextMenuPosition(undefined);
    setContextMenuClickedItem(undefined);
    setContextMenuClickedField(undefined);
  };

  const handleContextMenu = (event: React.MouseEvent<Element>) => {
    if (!canUseContextMenu) return;
    event.preventDefault();
    setContextMenuClickedItem(undefined);
    setContextMenuClickedField(getClickedField(event));
    setContextMenuPosition({ left: event.clientX, top: event.clientY });
  };

  const handleGridContextMenu = (event: React.MouseEvent<Element>) => {
    if (!canUseContextMenu) return;
    event.preventDefault();
    const clickedField = getClickedField(event);
    const rowElement: HTMLElement | null = (event.target as HTMLElement).closest('[data-id]');
    if (rowElement) {
      const rowId = rowElement.dataset.id;
      if (rowId !== undefined) {
        const rowModel = getRowById(rowId);
        if (rowModel) {
          setContextMenuClickedItem(rowModel);
          setContextMenuClickedField(clickedField);
          setContextMenuPosition({ left: event.clientX, top: event.clientY });
          return;
        }
      }
    }
    setContextMenuClickedField(clickedField);
    handleContextMenu(event);
  };

  return {
    contextMenuPosition,
    contextMenuClickedItem,
    contextMenuClickedField,
    handleContextMenu,
    handleGridContextMenu,
    handleCloseContextMenu,
  };
};
